import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

/**
 * F1.b — Detects when the user lands on /scripts coming from the onboarding gift flow.
 *
 * Reads from:
 *  · query param `?gift=<scriptId>` (stateless — survives refresh)
 *  · location.state.giftScriptId (richer — passed via navigate() from OnboardingPage)
 *
 * Returns `{ giftScriptId, clearGift }`:
 *  · giftScriptId: the ID of the gift script if present, else null
 *  · clearGift():  call to strip the marker from URL + state (prevents banner re-appearing on back-nav)
 */
export function useOnboardingGift() {
  const location = useLocation()
  const navigate = useNavigate()

  const queryGiftId = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return params.get('gift') || null
  }, [location.search])

  const stateGiftId = location.state?.giftScriptId || null

  const [active, setActive] = useState(null)

  useEffect(() => {
    const id = queryGiftId || stateGiftId
    if (id) setActive(id)
  }, [queryGiftId, stateGiftId])

  const clearGift = () => {
    setActive(null)
    // Strip ?gift=... from URL without triggering a nav change
    const params = new URLSearchParams(location.search)
    params.delete('gift')
    const nextSearch = params.toString()
    navigate(
      { pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' },
      { replace: true, state: {} }
    )
  }

  return { giftScriptId: active, clearGift }
}
